import { CSSProperties } from 'react';
import { TABLE_LOGO_NATIVE_WIDTH } from 'constants/gameConstants';

/**
 * EN-10520 — ONE type system for everything printed on the felt.
 *
 * The four felt elements (watermark logo, pot, blinds / game type, table+hand
 * number) were each styled and positioned on their own: different families,
 * different weights, three different whites, and three different anchors. That
 * is what produced the uneven spacing and the pot-over-logo collision.
 *
 * They now share this scale and one vertical group (see FeltMeta), reading top
 * to bottom as: **pot → logo → board → table/hand no → blinds/game type**.
 *
 * The treatment is deliberately TRANSLUCENT: felt text is context, not content,
 * so it recedes behind the cards and chips instead of competing with them. The
 * pot amount is the single exception — it stays in the brand accent, because it
 * IS content, and overrides `color` on top of these tokens.
 */

const FELT_FONT = 'Outfit, sans-serif';

/**
 * The single grey every felt readout is printed in — the tone the table/hand
 * line already used, now shared by the blinds, the game type and the whole
 * tournament block.
 *
 * There used to be three: 0.62 for the blinds, 0.5 for the tournament level,
 * 0.38 for the table/hand number. Three brightnesses reads as three unrelated
 * labels rather than one block of table reference data, and the brightest of
 * them competed with the cards and chips it sits under. Hierarchy inside the
 * group is now carried by SIZE and WEIGHT alone.
 */
export const FELT_TEXT_COLOR = 'rgba(255, 255, 255, 0.38)';

/**
 * Shared base — family, colour, tracking, case and the shadow that crisps text
 * against the felt. Everything below differs from it ONLY in size and weight,
 * which is what keeps the group reading as one system.
 */
export const feltTextBase: CSSProperties = {
  fontFamily: FELT_FONT,
  color: FELT_TEXT_COLOR,
  lineHeight: 1.3,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  textAlign: 'center',
  textShadow: '0 1px 2px rgba(0, 0, 0, 0.6)',
  userSelect: 'none'
};

/** Primary felt line — blinds / game type, and the tournament's name. */
export const feltPrimaryText: CSSProperties = {
  ...feltTextBase,
  fontSize: '0.8rem',
  fontWeight: 700
};

/** Secondary felt line — tournament level / countdown, next blinds. */
export const feltSecondaryText: CSSProperties = {
  ...feltTextBase,
  fontSize: '0.72rem',
  fontWeight: 600
};

/** Quietest felt line — table number / hand number. Reference data only. */
export const feltMetaText: CSSProperties = {
  ...feltTextBase,
  fontSize: '0.68rem',
  fontWeight: 400
};

/** Even vertical rhythm between the felt lines — one value, not four margins. */
export const FELT_LINE_GAP = '4px';

/**
 * The under-board group: table/hand number, then the blinds / game-type line.
 * Absolute in portrait (pinned under the board so the board itself never moves
 * as the group's height changes) and in-flow in landscape.
 */
export const feltMetaGroupStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: FELT_LINE_GAP,
  // Scale with the table like the cards and pot, floored so it stays legible.
  // The player's Table View Size is inside `--seat-scale` now, so it arrives
  // through this same expression rather than as a second multiplier.
  // transformOrigin top keeps the group tucked under the board (open felt below
  // → nothing to collide with).
  transform: 'scale(max(0.8, var(--seat-scale, 1)))',
  transformOrigin: 'top center',
  pointerEvents: 'none'
};

/**
 * Felt watermark sizing.
 *
 * `width` is a percentage of the inner felt so the mark tracks the table, but it
 * is CAPPED at the asset's native width — a 1440px table used to blow a 200px
 * PNG up past 300px, which is exactly how it went soft. The portrait percentage
 * is also what keeps the mark's top edge clear of the pot pill floating above
 * the board.
 */
export const feltLogoWidth = (isPortrait: boolean): string =>
  `min(${isPortrait ? 52 : 32}%, ${TABLE_LOGO_NATIVE_WIDTH}px)`;
